// 8-bit character each student builds on the welcome page (Dew, 2026-10-18):
// a Thai food mascot (food + face + hat) or a person (skin + hair + shirt), both can hold an item.
// Hand-drawn 16x16 pixel parts, no generated art. Pure and SDK-free.

export type Avatar = {
  person: boolean;
  food: number; face: number; hat: number;
  skin: number; hair: number; hairColor: number; shirt: number;
  item: number;
};

export const FOODS = ["กุ้ง", "หมูกรอบ", "ซาลาเปา", "ไข่ดาว", "ชาไข่มุก", "ไก่ทอด", "มะม่วง"];
export const FACES = ["ยิ้ม", "แว่นดำ", "แก้มแดง"];
export const HATS = ["ไม่ใส่", "หมวกเชฟ", "มงกุฎ", "หมวกรับปริญญา", "หมวกแก๊ป", "โบว์"];
export const SKINS = ["#F5D0A9", "#E0AC7E", "#C68642", "#8D5524"];
export const HAIR_COLORS = ["#1B1B1B", "#5A3825", "#D9A441", "#E8739E", "#4FB3FF"];
export const SHIRTS = ["#23E88A", "#4FB3FF", "#FF8A3D", "#B98CFF", "#2A2F36", "#FF5A5A"];
export const HAIR_STYLES = ["สั้น", "ตั้ง", "ยาว", "หมวก"];
export const ITEMS = ["ไม่ถือ", "โน้ตบุ๊ก", "กาแฟ", "ดาว", "จรวด"];

export const DEFAULT_AVATAR: Avatar = { person: false, food: 0, face: 0, hat: 0, skin: 1, hair: 0, hairColor: 0, shirt: 0, item: 0 };

// Bodies keep rows 0-4 mostly free for hats. E = eye, M = mouth; other letters see palette().
const FOOD: string[][] = [
  [ // shrimp
    "................",
    "................",
    "................",
    "....r......r....",
    ".....r....r.....",
    ".....OOOOOO.....",
    "....OOOOOOOO....",
    "...OOEOOOOEOO...",
    "...OOOOMMOOOO...",
    "...oooooooooo...",
    "...OOOOOOOOOO...",
    "....oooooooo....",
    ".....OOOOOO.....",
    "......oooo......",
    ".....OO..OO.....",
    "....OOO..OOO....",
  ],
  [ // crispy pork
    "................",
    "................",
    "................",
    "................",
    "................",
    "...CCCCCCCCCC...",
    "..CCbCCbCCbCCC..",
    "..FFFFFFFFFFFF..",
    "..PPPPPPPPPPPP..",
    "..PPEPPPPPPEPP..",
    "..PPPPPMMPPPPP..",
    "..PPPPPPPPPPPP..",
    "..FFFFFFFFFFFF..",
    "..PPPPPPPPPPPP..",
    "...PPPPPPPPPP...",
    "....KK....KK....",
  ],
  [ // salapao (steamed bun)
    "................",
    "................",
    "................",
    "................",
    "................",
    ".......rr.......",
    "......WDDW......",
    "....WWWDWWWW....",
    "...WWWWWWWWWW...",
    "..WWWWWWWWWWWW..",
    "..WWEWWWWWWEWW..",
    "..WWWWWMMWWWWW..",
    "..WWWWWWWWWWWW..",
    "...WWWWWWWWWW...",
    "....DDDDDDDD....",
    "................",
  ],
  [ // fried egg
    "................",
    "................",
    "................",
    "................",
    "................",
    "....WWWW........",
    "..WWWWWWWWW.....",
    ".WWWWYYYYWWWW...",
    ".WWWYYYYYYWWWW..",
    "WWWYYEYYEYYWWWW.",
    "WWWYYYMMYYYWWW..",
    ".WWYYYYYYYYWWW..",
    ".WWWYYYYYYWWWW..",
    "..WWWWWWWWWWW...",
    "....WWWWWWW.....",
    "................",
  ],
  [ // bubble tea
    "................",
    "................",
    "................",
    "..........K.....",
    ".........K......",
    "....WWWWWKWW....",
    "...WWWWWWWWWW...",
    "...TTTTTTTTTT...",
    "...TTETTTTETT...",
    "...TTTTMMTTTT...",
    "....TTTTTTTT....",
    "....TTTTTTTT....",
    "....TKTTKTKT....",
    ".....KTKTKT.....",
    ".....TTTTTT.....",
    "................",
  ],
  [ // fried chicken drumstick
    "................",
    "................",
    "................",
    "................",
    ".....bbbbb......",
    "....bCbbbbbb....",
    "...bbbbbbCbbb...",
    "...bbEbbbbEbb...",
    "...bbbbMMbbbb...",
    "...bbCbbbbbbb...",
    "....bbbbbbbb....",
    ".....bbbbbb.....",
    ".......WW.......",
    ".......WW.......",
    "......WWWW......",
    "......W..W......",
  ],
  [ // mango
    "................",
    "................",
    "................",
    "................",
    ".........gg.....",
    "........gg......",
    ".....YYYYY......",
    "....YYYYYYYY....",
    "...YYEYYYYEYY...",
    "...YYYYMMYYYYY..",
    "...YYYYYYYYYYY..",
    "...YYYYYYYYYYY..",
    "....YYYYYYYYY...",
    ".....yyyyyyy....",
    "......yyyy......",
    "................",
  ],
];

// Letters: H hair, S skin, E eye, M mouth, T shirt, then item colors (see palette()).
const PERSON = [
  "................",
  "................",
  "................",
  "................",
  "....SSSSSSSS....",
  "...SSSSSSSSSS...",
  "...SSESSSSESS...",
  "...SSSSSSSSSS...",
  "...SSSSMMSSSS...",
  "....SSSSSSSS....",
  "......SSSS......",
  "...TTTTTTTTTT...",
  "..TTTTTTTTTTTT..",
  ".TTTTTTTTTTTTTT.",
  ".TTTTTTTTTTTTTT.",
  ".TTTTTTTTTTTTTT.",
];

// Each hair style paints rows 0..10 on top of the face ("." = leave as is).
const HAIR: string[][] = [
  [ // short
    "................",
    ".....HHHHHH.....",
    "....HHHHHHHH....",
    "...HHHHHHHHHH...",
    "...HHSSSSSSHH...",
  ],
  [ // spiky
    "....H..H..H.....",
    "....HH.HH.HH....",
    "...HHHHHHHHHH...",
    "...HHHHHHHHHH...",
    "...HSSSSSSSSH...",
  ],
  [ // long
    "................",
    ".....HHHHHH.....",
    "....HHHHHHHH....",
    "...HHHHHHHHHH...",
    "..HHSSSSSSSSHH..",
    "..HSSSSSSSSSSH..",
    "..HSSESSSSESSH..",
    "..HSSSSSSSSSSH..",
    "..HSSSSMMSSSSH..",
    "..HHSSSSSSSSHH..",
    "..HH..SSSS..HH..",
  ],
  [ // cap (cap uses the hair colour)
    "................",
    "....HHHHHHHH....",
    "...HHHHHHHHHH...",
    "...HHHHHHHHHH...",
    "..HHHHHHHHHHHHH.",
  ],
];

// Hats paint rows 0..4 on top of the body ("." = leave as is).
const HAT: string[][] = [
  [],
  [ // chef
    "....WW.WW.WW....",
    "...WWWWWWWWWW...",
    "....WWWWWWWW....",
    "....WWWWWWWW....",
    "....DDDDDDDD....",
  ],
  [ // crown
    "................",
    "................",
    "....Y..YY..Y....",
    "....YYYYYYYY....",
    "....YRYYYYRY....",
  ],
  [ // graduation cap
    "................",
    "......KKKK......",
    ".KKKKKKKKKKKKKK.",
    "....KKKKKKKKY...",
    "....KKKKKKKK.Y..",
  ],
  [ // cap
    "................",
    "................",
    ".....GGGGGG.....",
    "....GGGGGGGG....",
    "....GGGGGGGGgggg",
  ],
  [ // bow
    "................",
    "................",
    "....ppp.ppp.....",
    ".....ppppp......",
    "....ppp.ppp.....",
  ],
];

// Items sit bottom-right, rows 10..15, cols 10..15.
const ITEM: string[][] = [
  [],
  ["......", "......", "WWWWW.", "WKKKW.", "WWWWW.", "DDDDDD"], // laptop
  ["......", "......", ".WWW..", ".BBBW.", ".BBBW.", ".BBB.."], // coffee
  ["......", "..Y...", ".YYY..", "YYYYY.", ".Y.Y..", "......"], // star
  ["...R..", "..WWW.", "..WKW.", "..WWW.", ".RWWWR", "..O.O."], // rocket
];

function paint(rows: string[][], layer: string[], top: number, left: number) {
  layer.forEach((line, y) =>
    line.split("").forEach((c, x) => {
      if (c !== ".") rows[top + y][left + x] = c;
    })
  );
}

export function avatarGrid(a: Avatar): string[] {
  if (a.person) {
    const rows = PERSON.map((r) => r.split(""));
    paint(rows, HAIR[a.hair % HAIR.length], 0, 0);
    paint(rows, ITEM[a.item % ITEM.length], 10, 10);
    return rows.map((r) => r.join(""));
  }
  const rows = FOOD[a.food % FOOD.length].map((r) => r.split(""));
  // Faces work off wherever this food's eyes are drawn.
  const eyes: [number, number][] = [];
  rows.forEach((r, y) => r.forEach((c, x) => c === "E" && eyes.push([y, x])));
  if (eyes.length === 2) {
    const [[y, x1], [, x2]] = eyes;
    if (a.face === 1) for (let x = x1 - 1; x <= x2 + 1; x++) rows[y][x] = "K"; // sunglasses
    if (a.face === 2) rows[y + 1][x1 - 1] = rows[y + 1][x2 + 1] = "p"; // blush
  }
  paint(rows, HAT[a.hat % HAT.length], 0, 0);
  paint(rows, ITEM[a.item % ITEM.length], 10, 10);
  return rows.map((r) => r.join(""));
}

const COLOURS: Record<string, string> = {
  E: "#1B1B1B", M: "#C0504D", K: "#2A2F36", W: "#FFF8EC", D: "#B8B2A6",
  O: "#FF8A3D", o: "#FFB27A", r: "#E85D3D", R: "#FF5A5A",
  C: "#A0522D", F: "#FFF3D6", P: "#F4A3A8", b: "#D9963A",
  Y: "#FFC83D", y: "#FFE48A", T: "#C89B6D", B: "#7A4A2A",
  G: "#23E88A", g: "#1F8F55", p: "#FF7A9A",
};

// Person letters H/S/T are the chosen hair, skin and shirt colours (T is tea on the food side).
export function palette(a?: Avatar): Record<string, string> {
  if (!a?.person) return COLOURS;
  return {
    ...COLOURS,
    H: HAIR_COLORS[a.hairColor % HAIR_COLORS.length],
    S: SKINS[a.skin % SKINS.length],
    T: SHIRTS[a.shirt % SHIRTS.length],
    W: "#E8EDF0",
    D: "#8A939A",
  };
}

/** Anything stored by an older/forged client becomes a valid avatar (indices clamped).
 *  Avatars saved before the food mascots (skin/hair, no food) stay a person. */
export function cleanAvatar(raw: unknown): Avatar {
  const r = (raw ?? {}) as Partial<Record<keyof Avatar, unknown>>;
  const pick = (v: unknown, n: number, d = 0) => (Number.isInteger(v) && (v as number) >= 0 && (v as number) < n ? (v as number) : d);
  return {
    person: r.person === true || (r.food === undefined && r.skin !== undefined),
    food: pick(r.food, FOOD.length),
    face: pick(r.face, FACES.length),
    hat: pick(r.hat, HAT.length),
    skin: pick(r.skin, SKINS.length, 1),
    hair: pick(r.hair, HAIR.length),
    hairColor: pick(r.hairColor, HAIR_COLORS.length),
    shirt: pick(r.shirt, SHIRTS.length),
    item: pick(r.item, ITEM.length),
  };
}

// Welcome-page question: what the student does now (pick one).
export const OCCUPATIONS = ["พนักงานประจำ", "ฟรีแลนซ์", "เจ้าของธุรกิจ", "ขายของออนไลน์", "ครีเอเตอร์ / ทำคอนเทนต์", "นักศึกษา", "อื่นๆ"];
